import { format, formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import type { Project } from '../shared/types';
import { TOAST_MESSAGES, ROLE_LABELS } from '../shared/constants/strings';

// ── Auth / Token Utilities ──────────────────────────────────────

const ENCODING_KEY = process.env.REACT_APP_OBFUSCATION_SALT || 'cs-fallback';
const TOKEN_PREFIX = 'cs';

const KEYS = {
  ACCESS_TOKEN: `${TOKEN_PREFIX}.accessToken`,
  REFRESH_TOKEN: `${TOKEN_PREFIX}.refreshToken`,
  SESSION: `${TOKEN_PREFIX}.session`,
  IS_LOGGED_IN: `${TOKEN_PREFIX}.isUserLoggedIn`,
  USER_ROLE: `${TOKEN_PREFIX}.userRole`,
} as const;

export interface StoredSession {
  isLoggedIn: boolean;
  userId: string;
  email: string;
  role: string;
  tenantId: string | null;
  project?: Partial<Project> | null;
  /**
   * The permission list the login response carried. Stored so a page refresh restores the same
   * access without a second round trip — the app has no endpoint to re-read it from.
   */
  permissions?: string[];
}

function encode(value: string): string {
  try {
    return btoa(
      encodeURIComponent(value)
        .split('')
        .map((c, i) => {
          const secretChar = ENCODING_KEY.charCodeAt(i % ENCODING_KEY.length);
          return String.fromCharCode(c.charCodeAt(0) ^ secretChar);
        })
        .join(''),
    );
  } catch {
    return '';
  }
}

function decode(encoded: string): string {
  try {
    return decodeURIComponent(
      atob(encoded)
        .split('')
        .map((c, i) => {
          const secretChar = ENCODING_KEY.charCodeAt(i % ENCODING_KEY.length);
          return String.fromCharCode(c.charCodeAt(0) ^ secretChar);
        })
        .join(''),
    );
  } catch {
    return '';
  }
}

export const secureStorage = {
  getAccessToken(): string | null {
    const encoded = localStorage.getItem(KEYS.ACCESS_TOKEN);
    if (!encoded) return null;
    return decode(encoded) || null;
  },

  setAccessToken(token: string): void {
    localStorage.setItem(KEYS.ACCESS_TOKEN, encode(token));
  },

  getRefreshToken(): string | null {
    const encoded = localStorage.getItem(KEYS.REFRESH_TOKEN);
    if (!encoded) return null;
    return decode(encoded) || null;
  },

  setRefreshToken(token: string): void {
    localStorage.setItem(KEYS.REFRESH_TOKEN, encode(token));
  },

  setTokens(accessToken: string, refreshToken: string): void {
    this.setAccessToken(accessToken);
    this.setRefreshToken(refreshToken);
  },

  // ── Session ──────────────────────────────────────────────────────

  setSession(session: StoredSession): void {
    localStorage.setItem(KEYS.SESSION, encode(JSON.stringify(session)));
    // Store plain values for easy access
    localStorage.setItem(KEYS.IS_LOGGED_IN, String(session.isLoggedIn));
    localStorage.setItem(KEYS.USER_ROLE, session.role);
  },

  getSession(): StoredSession | null {
    const encoded = localStorage.getItem(KEYS.SESSION);
    if (!encoded) return null;
    try {
      const decoded = decode(encoded);
      return decoded ? (JSON.parse(decoded) as StoredSession) : null;
    } catch {
      return null;
    }
  },

  isLoggedIn(): boolean {
    return localStorage.getItem(KEYS.IS_LOGGED_IN) === 'true' && this.hasTokens();
  },

  getRole(): string | null {
    return localStorage.getItem(KEYS.USER_ROLE);
  },

  // ── Cleanup ─────────────────────────────────────────────────────

  clearTokens(): void {
    localStorage.removeItem(KEYS.ACCESS_TOKEN);
    localStorage.removeItem(KEYS.REFRESH_TOKEN);
  },

  clearAll(): void {
    localStorage.removeItem(KEYS.ACCESS_TOKEN);
    localStorage.removeItem(KEYS.REFRESH_TOKEN);
    localStorage.removeItem(KEYS.SESSION);
    localStorage.removeItem(KEYS.IS_LOGGED_IN);
    localStorage.removeItem(KEYS.USER_ROLE);
  },

  hasTokens(): boolean {
    return !!this.getAccessToken();
  },
};

// ── Notification Utilities ──────────────────────────────────────

export function notifySuccess(message: string): void {
  toast.success(message);
}

export function notifyError(message: string): void {
  toast.error(message);
}

export function notifyWarning(message: string): void {
  toast(message, { icon: '⚠️' });
}

export function notifyInfo(message: string): void {
  toast(message, { icon: 'ℹ️' });
}

// ── API Error Utilities ─────────────────────────────────────────

/**
 * Pull the server's own error message out of a failed request so it can be shown verbatim
 * (e.g. "only the client who raised this ticket can close it"). Falls back to the supplied
 * message when the response carries nothing user-meaningful.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const data =
    (error as { response?: { data?: { message?: string; error?: string } } })?.response?.data ??
    (error as { data?: { message?: string; error?: string } })?.data;
  const serverMessage = data?.message || data?.error;
  if (typeof serverMessage === 'string' && serverMessage.trim()) {
    return serverMessage.trim();
  }

  // Axios' generic "Request failed with status code 403" tells the user nothing useful.
  const message = (error as { message?: string })?.message;
  if (typeof message === 'string' && message.trim() && !/^Request failed with status code/i.test(message)) {
    return message.trim();
  }

  return fallback;
}

export function getConnectionErrorMessage(error: unknown): string | null {
  const err = error as { code?: string; response?: { status?: number } };

  // No response at all — the request never reached the API.
  if (!err?.response) {
    return err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT'
      ? TOAST_MESSAGES.REQUEST_TIMEOUT
      : TOAST_MESSAGES.SERVER_UNREACHABLE;
  }

  const status = err.response.status ?? 0;
  if (status === 502 || status === 503 || status === 504) {
    return TOAST_MESSAGES.SERVER_UNAVAILABLE;
  }
  if (status >= 500) {
    return TOAST_MESSAGES.SERVER_ERROR;
  }

  return null;
}

// ── Date Formatting ─────────────────────────────────────────────

/**
 * Format a date string using the given date-fns pattern.
 * Defaults to 'MMM dd, yyyy'.
 */
export function formatDate(value?: string | Date | null, pattern = 'MMM dd, yyyy'): string {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return format(date, pattern);
}

/**
 * Return a human-readable relative time string (e.g. "3 minutes ago").
 */
export function formatRelativeTime(value?: string | Date | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return formatDistanceToNow(date, { addSuffix: true });
}

/**
 * Format a date as 'MMM dd, yyyy @ hh:mm a'.
 */
export function formatDateAndTime(value?: string | Date | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return format(date, 'MMM dd, yyyy @ hh:mm a');
}

/**
 * Format a date as 'yyyy-MM-dd' for server payloads.
 */
export function toServerDateFormat(value?: string | Date | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return format(date, 'yyyy-MM-dd');
}

// ── Currency / Number Formatting ────────────────────────────────

/**
 * Format a number as Indian currency (e.g. ₹1,234.56).
 */
export function formatCurrency(amount: number): string {
  return `₹${amount.toFixed(2)}`;
}

/**
 * Format a number as currency using the given ISO 4217 code (e.g. INR, USD).
 */
export function formatCurrencyWithCode(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

// ── String Utilities ────────────────────────────────────────────

/**
 * Extract initials from a full name string or first + last name (e.g. "John Doe" -> "JD", "Dev Test1" -> "DT").
 */
export function getInitials(firstName?: string | null, lastName?: string | null): string {
  const cleanFirst = typeof firstName === 'string' ? firstName.trim() : '';
  const cleanLast = typeof lastName === 'string' ? lastName.trim() : '';

  // Case 1: Both first and last names are explicitly provided
  if (cleanFirst && cleanLast) {
    return `${cleanFirst.charAt(0)}${cleanLast.charAt(0)}`.toUpperCase();
  }

  // Case 2: Single full name string or single name provided
  const singleName = cleanFirst || cleanLast;
  if (!singleName) {
    return '';
  }

  const parts = singleName.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const firstInitial = parts[0].charAt(0);
    const secondInitial = parts[1].charAt(0);
    return `${firstInitial}${secondInitial}`.toUpperCase();
  }

  return parts[0].charAt(0).toUpperCase();
}

/**
 * Truncate text to a maximum length, appending an ellipsis if truncated.
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

/**
 * Extract assignedTo name from an object ({ id, name }), string, or return '—' if empty/null.
 */
function relationName(value: unknown): string | null {
  if (typeof value === 'object' && value !== null) {
    const name = (value as { name?: string }).name;
    return name && name.trim() ? name.trim() : null;
  }
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  return null;
}

export function getAssignedToName(assignedTo: unknown): string {
  return relationName(assignedTo) ?? '—';
}

/** Who raised a ticket, sent either as an object ({ id, name }) or as a bare name string. */
export function getRaisedByName(raisedBy: unknown): string {
  return relationName(raisedBy) ?? '—';
}

/**
 * Read a project's display name off a relation the API returns either as an object or as a bare
 * name string. `fallback` is what to show when there is no project — pass '' where the caller
 * only renders the row for a project that exists.
 */
export function getProjectName(project: unknown, fallback = '—'): string {
  return relationName(project) ?? fallback;
}

/**
 * Format a string by replacing underscores with spaces and converting to Title Case
 * (e.g. "RECEIPT_VOUCHER" -> "Receipt Voucher", "DAY_END_SUMMARY" -> "Day End Summary").
 */
export function formatLabel(text?: string | null): string {
  if (!text) return '—';
  const clean = text.replace(/_/g, ' ').trim();
  return clean
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
/**
 * A role's display name. `ROLE_LABELS` covers the roles the app ships knowing about; anything
 * else — a role defined in Role Management, or one the backend added — is titled from its own
 * name, so `COMPANY_SUPPORT` reads as "Company Support" rather than as the raw enum.
 */
export function roleLabel(role?: string | null): string {
  if (!role) return '—';
  return ROLE_LABELS[role] ?? formatLabel(role);
}

export function formatProviderName(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Render a boolean flag as Yes / No for a table cell.
 *
 * `null` and `undefined` become "No" rather than an em dash: these flags are switches, and an
 * absent one is off, not unknown.
 */
export function formatBoolean(value?: boolean | null): string {
  return value ? 'Yes' : 'No';
}

// ── Regex Constants ─────────────────────────────────────────────

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const phoneRegex = /^\+?[1-9]\d{1,14}$/;

// ── Common Constants ────────────────────────────────────────────

export const TIMEZONE_OPTIONS = [
  { label: 'UTC', value: 'UTC' },
  { label: 'Eastern Time (US)', value: 'America/New_York' },
  { label: 'Central Time (US)', value: 'America/Chicago' },
  { label: 'Mountain Time (US)', value: 'America/Denver' },
  { label: 'Pacific Time (US)', value: 'America/Los_Angeles' },
  { label: 'London (GMT)', value: 'Europe/London' },
  { label: 'Berlin (CET)', value: 'Europe/Berlin' },
  { label: 'Tokyo (JST)', value: 'Asia/Tokyo' },
  { label: 'Sydney (AEST)', value: 'Australia/Sydney' },
];
